import imageCompression from "browser-image-compression";

/**
 * Image compression options for web optimization
 *
 * Strategy:
 * - Max dimensions: 2048px (for fullscreen lightbox - largest display size)
 * - JPEG quality: 75-85 (good balance between quality and file size)
 * - Strip metadata (EXIF, GPS, camera info) for privacy and size reduction
 *
 * Use cases:
 * - Gallery grid: 400-600px (but we serve max size, browser scales down)
 * - Listing view: 1200-1600px (but we serve max size, browser scales down)
 * - Fullscreen lightbox: 1600-2048px (max needed)
 *
 * By serving 2048px, we cover all use cases while keeping file sizes reasonable.
 */
export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeMB?: number;
  useWebWorker?: boolean;
  /** If false, always process (for fullsize: ensures higher quality even when source is small) */
  skipIfUnderTarget?: boolean;
}

const DEFAULT_OPTIONS: ImageCompressionOptions = {
  maxWidth: 2048,
  maxHeight: 2048,
  quality: 0.6, // 60% quality (lower quality for smaller file sizes)
  maxSizeMB: 10, // Keep same max size as before (fallback limit)
  useWebWorker: true, // Better performance
};

// Debug flag - set to true to enable detailed logging
const isDebugging = false;

/**
 * Compress and resize an image file
 *
 * @param file Original image file
 * @param options Compression options (optional)
 * @returns Compressed File object
 */
export async function compressImage(
  file: File,
  options: ImageCompressionOptions = {},
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  // Use maxSizeMB from options if provided, otherwise default to 0.7MB (700KB)
  const TARGET_SIZE_MB =
    opts.maxSizeMB !== undefined && opts.maxSizeMB < 10 ? opts.maxSizeMB : 0.7;
  const MAX_ITERATIONS = 15; // Increased iterations for more aggressive compression
  const TARGET_SIZE_BYTES = TARGET_SIZE_MB * 1024 * 1024; // Convert to bytes for precise comparison

  // Calculate original size once at the start
  const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);

  try {
    let currentFile = file;
    let currentQuality = opts.quality || 0.6;
    let currentMaxDimension = Math.max(
      opts.maxWidth || 2048,
      opts.maxHeight || 2048,
    );
    let iteration = 0;
    let currentSizeMB = file.size / (1024 * 1024);

    // If file is already under target size, return as-is (unless skipIfUnderTarget=false for fullsize)
    const shouldSkip = opts.skipIfUnderTarget !== false;
    if (shouldSkip && file.size <= TARGET_SIZE_BYTES) {
      if (isDebugging) {
      }
      return file;
    }

    // When skipIfUnderTarget=false (fullsize): file is small but we still process to apply quality/dimensions
    if (!shouldSkip && file.size <= TARGET_SIZE_BYTES) {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: TARGET_SIZE_MB,
        maxWidthOrHeight: Math.max(
          opts.maxWidth || 2048,
          opts.maxHeight || 2048,
        ),
        useWebWorker: opts.useWebWorker,
        fileType: file.type,
        initialQuality: opts.quality || 0.8,
      });
      return compressedFile;
    }

    // Start with lower quality if file is very large
    if (currentSizeMB > 2) {
      currentQuality = 0.4; // Start with 40% quality for very large files
    } else if (currentSizeMB > 1) {
      currentQuality = 0.5; // Start with 50% quality for large files
    }

    // Loop until file is under target size or max iterations reached
    while (currentFile.size > TARGET_SIZE_BYTES && iteration < MAX_ITERATIONS) {
      iteration++;

      // Gradually reduce quality with each iteration
      // Reduce by 0.05 each iteration (more gradual) for better control
      const qualityForIteration = Math.max(
        0.1,
        currentQuality - iteration * 0.05,
      );

      // Also reduce dimensions more aggressively if file is still too large
      if (iteration > 1 && currentFile.size > TARGET_SIZE_BYTES * 1.2) {
        // If file is still 20% over target, reduce dimensions
        currentMaxDimension = Math.max(
          800,
          Math.floor(currentMaxDimension * 0.9),
        );
      }

      // Compress the image with current quality and dimensions
      const compressedFile = await imageCompression(currentFile, {
        maxSizeMB: TARGET_SIZE_MB, // Set target size
        maxWidthOrHeight: currentMaxDimension,
        useWebWorker: opts.useWebWorker,
        fileType: currentFile.type, // Keep original file type (JPEG, PNG, etc.)
        initialQuality: qualityForIteration,
        // Strip metadata by default (EXIF, GPS, camera info)
        // browser-image-compression does this automatically for JPEG
      });

      const newSizeMB = compressedFile.size / (1024 * 1024);
      if (isDebugging) {
      }

      // Check if we made progress
      if (compressedFile.size >= currentFile.size && iteration > 1) {
        // If compression didn't reduce size, we've hit a limit - break to avoid infinite loop
        if (isDebugging) {
        }
        break;
      }

      currentFile = compressedFile;
      currentSizeMB = newSizeMB;
      currentQuality = qualityForIteration;
    }

    // Log compression result (helpful for debugging)
    if (isDebugging) {
      const compressedSizeMB = (currentFile.size / (1024 * 1024)).toFixed(2);
      const reductionPercent = (
        (1 - currentFile.size / file.size) *
        100
      ).toFixed(1);

      if (currentFile.size > TARGET_SIZE_BYTES) {
      } else {
      }
    }

    return currentFile;
  } catch (error) {
    // If compression fails, return original file (graceful degradation)
    // This could happen with corrupted files or unsupported formats
    throw new Error(
      `Failed to compress image: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

/**
 * Compress image with thumbnail-specific settings (higher quality for thumbnail)
 *
 * @param file Original image file
 * @param options Compression options (optional) - can override defaults
 * @returns Compressed File object with slightly higher quality for thumbnails
 */
export async function compressImageForThumbnail(
  file: File,
  options: ImageCompressionOptions = {},
): Promise<File> {
  return compressImage(file, {
    quality: 0.7, // Slightly higher quality for thumbnail (70% vs 60% for regular)
    maxWidth: 2048,
    maxHeight: 2048,
    ...options, // Allow overriding options (e.g., maxSizeMB)
  });
}

/**
 * Compress image for preview/small version
 * Used for listings, carousels, etc. where many images are displayed at once
 *
 * Target: 400×300 eller 480×360 px, WebP preferred, 20–50 KB, quality ≈ 75
 *
 * @param file Original image file
 * @param options Compression options (optional) - can override defaults
 * @returns Compressed File object optimized for preview (max 50KB)
 */
export async function compressImageForPreview(
  file: File,
  options: ImageCompressionOptions = {},
): Promise<File> {
  return compressImage(file, {
    maxSizeMB: 0.05, // 50KB max for preview (target: 20-50KB)
    maxWidth: 480, // 480×360 px dimensions for preview
    maxHeight: 360,
    quality: 0.7, // 75% quality for preview
    ...options, // Allow overriding options
  });
}

/**
 * Compress image for full size version
 * Used for detail views, galleries where quality matters more
 *
 * Target: 1200–1600 px, 150–200 KB, quality ≈ 75–85
 *
 * @param file Original image file
 * @param options Compression options (optional) - can override defaults
 * @returns Compressed File object optimized for full size (max 200KB)
 */
export async function compressImageForFullSize(
  file: File,
  options: ImageCompressionOptions = {},
): Promise<File> {
  return compressImage(file, {
    maxSizeMB: 0.2, // 200KB max for full size (target: 150-200KB)
    maxWidth: 1600, // 1600px max dimension for full size
    maxHeight: 1600,
    quality: 0.8, // 80% quality for full size (higher than preview)
    skipIfUnderTarget: false, // Always process so fullsize differs from preview (avoids same 34kb for both)
    ...options, // Allow overriding options
  });
}
